import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Select } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';
import { ItemTypeEnum } from '@share/common-types';

import { SmallBanner } from '../components/small-banner';
import { CertificateCard, CertificateCardSkeleton } from '../components/certificate-card';
import { DEFAULT_BANNER_BACKGROUND } from '../components/banner/utils';
import { useCertificateTemplatesCatalog, type CertificateTemplatesSortOption } from '../hooks';
import {
  CATALOG_PAGE_SIZE,
  SKELETON_CARD_COUNT,
  ITEM_TYPE_OPTIONS,
  SORT_OPTIONS_CONFIG,
} from './constants';

import styles from './styles.module.scss';
import { isFilterActive, parseItemTypesFromSearch, toggleFilter } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export const CertificatesCatalogWrapper = (): React.ReactElement => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();
  const location = useLocation();
  const { accountUsername } = useParams<{ accountUsername?: string }>();

  const [selectedItemTypes, setSelectedItemTypes] = useState<ItemTypeEnum[]>(() =>
    parseItemTypesFromSearch(location.search),
  );
  const [sortOption, setSortOption] = useState<CertificateTemplatesSortOption>('priceAsc');

  const { items, hasNextPage, loading, loadingMore, error, refetch, loadMore } =
    useCertificateTemplatesCatalog({
      pageSize: CATALOG_PAGE_SIZE,
      itemTypes: selectedItemTypes,
      sortOption,
    });

  const sortSelectOptions = useMemo(
    () =>
      SORT_OPTIONS_CONFIG.map((o) => ({
        value: o.value,
        label: intl.formatMessage({ id: o.labelId }),
      })),
    [intl],
  );

  const certificatesPath = accountUsername
    ? `/${accountUsername}${Routes.Certificates}`
    : Routes.Certificates;

  const handleBackClick = useCallback(() => {
    history.push(certificatesPath);
  }, [history, certificatesPath]);

  const handleFilterClick = useCallback((option: (typeof ITEM_TYPE_OPTIONS)[number]) => {
    setSelectedItemTypes((prev) => toggleFilter(prev, option));
  }, []);

  const handleSortChange = useCallback((value: CertificateTemplatesSortOption) => {
    setSortOption(value);
  }, []);

  const detailPathBase = accountUsername
    ? `/${accountUsername}${Routes.CertificateTemplateDetails}`
    : Routes.CertificateTemplateDetails;

  const handleCardClick = useCallback(
    (certificateId: string) => {
      history.push(`${detailPathBase}/${certificateId}`);
    },
    [history, detailPathBase],
  );

  const paymentPath = accountUsername ? `/${accountUsername}/certificates` : '/certificates';

  const handleBuyCertificate = useCallback(
    (certificateId: string) => {
      history.push(`${paymentPath}/${certificateId}/payment`);
    },
    [history, paymentPath],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || loading || loadingMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, loading, loadingMore, loadMore]);

  const backLabel = intl.formatMessage({ id: 'certificates.backToCatalog' });
  const catalogBannerAlt = intl.formatMessage({ id: 'certificates.catalog.banner.imageAlt' });
  const filterAria = intl.formatMessage({ id: 'certificates.catalog.filter.aria' });
  const sortCertificatesAria = intl.formatMessage({ id: 'certificates.catalog.sort.aria' });

  return (
    <div className={styles.wrapper}>
      <SmallBanner
        title={intl.formatMessage({ id: 'certificates.catalog.banner.title' })}
        description={intl.formatMessage({ id: 'certificates.catalog.banner.description' })}
        backgroundImageSrc={DEFAULT_BANNER_BACKGROUND}
        backgroundImageAlt={catalogBannerAlt}
      />

      <div ref={contentRef} className={styles.content}>
        <a
          href={certificatesPath}
          className={styles.backLink}
          onClick={(e) => {
            e.preventDefault();
            handleBackClick();
          }}
          aria-label={backLabel}
        >
          {backLabel}
        </a>

        <div className={styles.toolbar}>
          <div className={styles.filters} role="group" aria-label={filterAria}>
            {ITEM_TYPE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={
                  isFilterActive(option, selectedItemTypes)
                    ? styles.filterPillActive
                    : styles.filterPill
                }
                onClick={() => handleFilterClick(option)}
                aria-pressed={isFilterActive(option, selectedItemTypes)}
                aria-label={intl.formatMessage({ id: option.labelId })}
              >
                {option.icon && (
                  <FontAwesomeIcon className={styles.buttonIcon} icon={option.icon} />
                )}
                {intl.formatMessage({ id: option.labelId })}
              </button>
            ))}
          </div>

          <div className={styles.sortSearch}>
            <label htmlFor="certificates-sort" className={styles.sortLabel}>
              <FormattedMessage id="certificates.catalog.sort.label" />
            </label>
            <Select<CertificateTemplatesSortOption>
              id="certificates-sort"
              value={sortOption}
              onChange={handleSortChange}
              options={sortSelectOptions}
              className={styles.sortSelect}
              getPopupContainer={(node) => node.parentElement ?? document.body}
              aria-label={sortCertificatesAria}
            />
          </div>
        </div>

        {loading && (
          <div className={styles.grid} role="status" aria-busy="true">
            {Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
              <CertificateCardSkeleton key={i} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className={styles.stateBlock}>
            <p className={styles.errorText}>{error}</p>
            <BlueButton onClick={refetch}>
              <FormattedMessage id="certificates.landing.retry" />
            </BlueButton>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className={styles.stateBlock}>
            <p className={styles.emptyText}>
              <FormattedMessage id="certificates.catalog.empty" />
            </p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className={styles.grid}>
              {items.map((item) => (
                <CertificateCard
                  key={item.id}
                  item={item}
                  imageUrl={item.imageUrl}
                  onCardClick={() => handleCardClick(item.id)}
                  onBuyCertificate={() => handleBuyCertificate(item.id)}
                />
              ))}
            </div>
            {loadingMore && (
              <div className={styles.loadingMore} role="status" aria-busy="true">
                {Array.from({ length: 4 }, (_, i) => (
                  <CertificateCardSkeleton key={`more-${i}`} />
                ))}
              </div>
            )}
            <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />
          </>
        )}
      </div>
    </div>
  );
};
